// Mobile phone validation: FR, BE, CH, LU.
// Used by the public inscription form and the member settings page.

export type PhoneCountryCode = "FR" | "BE" | "CH" | "LU";

export interface PhoneCountry {
  code: PhoneCountryCode;
  label: string;
  flag: string;
  dial: string;          // e.g. "+33"
  placeholder: string;   // shown in the local-number input
}

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: "FR", label: "France",     flag: "🇫🇷", dial: "+33",  placeholder: "06 12 34 56 78" },
  { code: "BE", label: "Belgique",   flag: "🇧🇪", dial: "+32",  placeholder: "04 70 12 34 56" },
  { code: "CH", label: "Suisse",     flag: "🇨🇭", dial: "+41",  placeholder: "078 123 45 67" },
  { code: "LU", label: "Luxembourg", flag: "🇱🇺", dial: "+352", placeholder: "06 621 123 456" },
];

export const PHONE_EXAMPLES =
  "Formats acceptés : 06 12 34 56 78 (FR), 04XX XX XX XX (BE), 07X XXX XX XX (CH), 06XX XXX XXX (LU). Le 0 initial est optionnel.";

export const stripPhone = (raw: string): string => raw.replace(/[\s.\-()]/g, "");

/**
 * Validate and normalize a mobile phone number for FR, BE, CH, LU.
 * Returns the canonical stored form (international compact, e.g. "+33612345678")
 * on success, or null on failure.
 * - FR  → +33[67]XXXXXXXX
 * - BE  → +324XXXXXXXX
 * - CH  → +417[5-9]XXXXXXX
 * - LU  → +3526XXXXXXXX
 */
export const validateAndNormalizePhone = (raw: string): string | null => {
  const s = stripPhone(raw);
  // France
  if (/^\+33[67][0-9]{8}$/.test(s)) return s;
  if (/^0[67][0-9]{8}$/.test(s)) return "+33" + s.slice(1);
  // Belgium
  if (/^\+324[0-9]{8}$/.test(s)) return s;
  if (/^04[0-9]{8}$/.test(s)) return "+32" + s.slice(1);
  // Switzerland
  if (/^\+417[5-9][0-9]{7}$/.test(s)) return s;
  if (/^07[5-9][0-9]{7}$/.test(s)) return "+41" + s.slice(1);
  // Luxembourg
  if (/^\+3526[0-9]{8}$/.test(s)) return s;
  if (/^6[0-9]{8}$/.test(s)) return "+352" + s;
  return null;
};

export const isValidMobilePhone = (raw: string): boolean =>
  validateAndNormalizePhone(raw) !== null;

/**
 * Validate + normalize a local-number entry given an explicit country.
 * Accepts the local number with or without a leading 0 / country code.
 * Returns the international compact form (e.g. "+33612345678").
 */
export const validateAndNormalizeByCountry = (
  country: PhoneCountryCode,
  rawLocal: string,
): string | null => {
  const s = stripPhone(rawLocal);
  // If user pasted full international form, defer to the generic validator.
  if (s.startsWith("+")) return validateAndNormalizePhone(s);

  // Strip a single leading 0 (national trunk prefix).
  const digits = s.replace(/^0/, "");

  switch (country) {
    case "FR":
      if (/^[67][0-9]{8}$/.test(digits)) return "+33" + digits;
      return null;
    case "BE":
      if (/^4[0-9]{8}$/.test(digits)) return "+32" + digits;
      return null;
    case "CH":
      if (/^7[5-9][0-9]{7}$/.test(digits)) return "+41" + digits;
      return null;
    case "LU":
      if (/^6[0-9]{8}$/.test(digits)) return "+352" + digits;
      return null;
  }
};
